/**
 * An action's failure in place (TMM-B3, TMMRSL-0071): the translated message, then - when the
 * server sent a request id - the same selectable "Reference: {id}" caption `ApiErrorState` shows
 * for a failed load, so a message that says "contact support with the reference" has one to quote.
 * Every dialog and editor shows its failed submit through this, never a bare `Alert`.
 */
import { Alert, Typography, type AlertProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MONO } from '@/theme/tokens';

/** The reference caption on its own, for a caller that lays the alert out itself. */
export function ErrorReference({ requestId }: { requestId: string | undefined }) {
  const { t } = useTranslation();
  if (!requestId) return null;
  return (
    <Typography
      variant="caption"
      component="p"
      sx={{ fontFamily: MONO, userSelect: 'text', mt: 0.5 }}
    >
      {t('errors.reference', { id: requestId })}
    </Typography>
  );
}

export interface ActionErrorAlertProps extends Omit<AlertProps, 'children' | 'severity'> {
  /** An `ActionError` (or any `{ message, requestId }`); nothing renders for `null`. */
  error: { message: string; requestId?: string | undefined } | null;
}

export function ActionErrorAlert({ error, ...rest }: ActionErrorAlertProps) {
  if (error === null) return null;
  return (
    <Alert severity="error" {...rest}>
      {error.message}
      <ErrorReference requestId={error.requestId} />
    </Alert>
  );
}
